<template>
  <div>
    <div class="ts-flex ts-justify-between">
      <div class="ts-flex ts-space-x-3">
        <label class="ts-shadow-rounded ts-uppercase ts-p-2 ts-rounded-md">{{
          $t('wholesale.item')
        }}</label>
        <a
          href="#"
          class="ts-shadow-rounded ts-uppercase ts-px-4 ts-rounded-md ts-bg-blue-400 ts-text-white ts-flex ts-items-center ts-no-underline hover:ts-no-underline"
          style="height: 31px"
          @click.prevent="getProductBalance"
        >
          <span class="ts-mr-2">{{ $t('saleContract.getBalance') }}</span>
          <i class="fas fa-balance-scale"></i>
        </a>
      </div>
      <AddAutoAfterSearchSerial v-model="model" @calculate="calculate" />
    </div>
    <div class="max-h">
      <table class="ts-w-full">
        <thead>
          <tr>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-4"
            >
              {{ $t('wholesale.no') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-64"
            >
              {{ $t('wholesale.product') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-20"
            >
              {{ $t('wholesale.maxMin') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-16"
            >
              {{ $t('wholesale.quantity') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-16"
            >
              {{ $t('wholesale.uom') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-20"
            >
              {{ $t('wholesale.upKhr') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-20"
            >
              {{ $t('wholesale.unitPrice') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-12"
            >
              {{ $t('wholesale.discountType') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-12"
            >
              {{ $t('wholesale.discountRate') }}
            </th>
            <!-- vatRate header: always displayed now -->
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-20 ts-text-center"
            >
              {{ $t('wholesale.vatRate') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-w-32"
            >
              {{ $t('wholesale.grandTotal') }}
            </th>
            <th
              class="ts-border ts-border-gray-300 ts-uppercase ts-px-2 ts-py-2 ts-whitespace-no-wrap ts-text-center ts-w-20"
            >
              {{ $t('actions') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(product, index) in model.product_detail"
            :key="index"
            class="ts-border-b ts-border-gray-300"
          >
            <td class="ts-text-center">{{ index + 1 }}</td>
            <td
              class="ts-px-2 ts-py-1 ts-cursor-pointer"
              @click.prevent="showBalance(product)"
            >
              <a-popover :title="$t('wholesale.serial')" placement="bottomLeft">
                <template slot="content">
                  <p
                    v-for="(s, i) in product.serial_list"
                    :key="i"
                    class="ts-text-red-600"
                  >
                    {{ s.label }}
                  </p>
                </template>
                <p class="ts-mb-0 ts-font-extrabold ts-text-blue-600">
                  <i class="fab fa-product-hunt"></i>
                  {{
                    locale == 'kh'
                      ? product.product_name_kh
                        ? product.product_name_kh
                        : product.product_name_rn
                      : product.product_name_en
                  }}
                </p>
                <p class="ts-mb-0">
                  <i class="fas fa-barcode"></i> {{ product.barcode }}
                </p>
                <p class="ts-mb-0">
                  <i class="fas fa-balance-scale"></i
                  ><b> {{ product.balance }}</b>
                </p>
              </a-popover>
              <div
                class="validate"
                v-if="errors.has('product_detail.' + index + '.serial_list')"
              >
                {{ errors.first('product_detail.' + index + '.serial_list') }}
              </div>
            </td>
            <td>
              <div class="form-control ts-text-center">
                <p class="ts-flex ts-justify-between">
                  <span>{{ getMax(product.product_id) }}</span>
                  <span>{{ getMin(product.product_id) }}</span>
                </p>
              </div>
            </td>
            <td>
              <input
                v-model.number="product.quantity"
                type="number"
                class="form-control ts-text-center"
                :readonly="product.has_serial || isNotAllowToEdit(product)"
                @input="calculate()"
              />
              <div
                class="ts-text-red-600"
                v-if="errors.has('product_detail.' + index + '.quantity')"
              >
                {{ errors.first('product_detail.' + index + '.quantity') }}
              </div>
            </td>
            <td>
              <a-select
                v-model.number="product.uom_id"
                class="ts-w-full"
                ref="uom"
                :class="{
                  'is-invalid': errors.has(
                    'product_detail.' + index + '.uom_id'
                  )
                }"
                :disabled="isNotAllowToEdit(product)"
                @change="onUomChange(product)"
              >
                <a-select-option
                  v-for="(u, index) in getUom(product.product_id)"
                  :key="index"
                  :value="u.uom_id"
                  >{{ u.uom_name_en }}</a-select-option
                >
              </a-select>
            </td>
            <!-- up_khr cell -->
            <td class="ts-w-32">
              <ts-currency-input
                :value="product.amount_in_khr"
                currency="៛"
                class="form-control ts-text-right"
                @input="onKhrInput(product, $event)"
              />
            </td>

            <!-- unitPrice cell -->
            <td class="ts-w-32">
              <ts-currency-input
                :value="product.unit_price"
                :precision="8"
                class="form-control ts-text-right"
                @input="onUpUsdInput(product, $event)"
                :readonly="isNotAllowToEdit(product)"
              />
            </td>
            <td>
              <select
                class="form-control"
                ref="discountType"
                v-model="product.discount_type"
                :disabled="isNotAllowToEdit(product)"
                @change="calculate(product)"
              >
                <option value="%">%</option>
                <option :value="default_currency_sign">{{
                  default_currency_sign
                }}</option>
              </select>
            </td>
            <td>
              <input
                v-model.number="product.discount_rate"
                type="text"
                ref="discountRate"
                class="form-control"
                @input="calculate()"
                :readonly="isNotAllowToEdit(product)"
              />
            </td>
            <!-- vatRate cell: always displayed now -->
            <td class="ts-text-center">
              <template v-if="product.vat_eligible">
                <small class="ts-text-blue-600">{{
                  $t('wholesale.byCustomer')
                }}</small>
              </template>
              <template v-else>
                <small class="ts-text-gray-500">{{
                  $t('wholesale.byGovernment')
                }}</small>
              </template>
            </td>
            <td>
              <ts-currency-input
                :value="product.grand_total"
                :precision="8"
                class="ts-border ts-w-full ts-border-gray-300 ts-rounded-sm ts-p-2 ts-text-right ts-outline-none ts-font-extrabold"
                disabled
              />
            </td>
            <td class="ts-text-left ts-pl-4 ts-space-x-3">
              <a
                v-if="model.customer_id != null"
                href="#"
                class="ts-text-blue-700"
                @click.prevent="viewPrice(product)"
              >
                <i class="fa fa-dollar-sign ts-mt-2"></i>
              </a>
              <a
                v-if="!isNotAllowToEdit(product)"
                href="#"
                class="ts-text-red-600"
                @click.prevent="removeRow(product)"
              >
                <i class="fa fa-trash"></i>
              </a>
              <a
                href="#"
                v-if="product.has_serial"
                @click.prevent="addSerialProduct(product)"
              >
                <i class="fas fa-list"></i>
              </a>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <!-- colspan fixed at 12 now that vatRate col is always visible -->
            <td class="ts-bg-blue-600" colspan="12"></td>
          </tr>
          <tr class="ts-bg-gray-300">
            <td
              class="ts-px-2 ts-py-1 ts-font-extrabold ts-uppercase"
              colspan="3"
            >
              <span>{{ $t('wholesale.rowNum') }} : </span
              >{{ $formatNumber(numRow) }}
            </td>
            <td
              class="ts-px-2 ts-py-1 ts-font-extrabold ts-text-red-600 ts-uppercase"
            >
              <span>{{ $t('wholesale.qty') }} : </span
              >{{ $formatNumber(countQty) }}
            </td>
            <!-- colspan fixed at 6 -->
            <td class="ts-text-right ts-px-2 ts-py-1" colspan="6">
              <span class="ts-font-extrabold ts-text-red-600 ts-uppercase"
                >{{ $t('wholesale.total') }} :
              </span>
            </td>
            <td class="ts-text-right ts-font-extrabold ts-text-red-600">
              <input
                type="text"
                class="ts-w-full ts-p-2 ts-text-right ts-font-extrabold ts-text-red-700 ts-outline-none"
                :value="
                  totalAmountUSDPrice
                    ? $defaultCurrencyWithCode(totalAmountUSDPrice, 8)
                    : 0
                "
                readonly
              />
            </td>
            <td></td>
          </tr>
          <tr>
            <td colspan="3" rowspan="5">
              <textarea
                v-model.trim="model.remark"
                rows="5"
                class="form-control"
                :placeholder="$t('wholesale.remark')"
              ></textarea>
            </td>
            <!-- colspan fixed at 7 -->
            <td colspan="7" class="ts-text-right">
              <div
                style="width: 17rem"
                class="ts-bg-blue-600 ts-text-white ts-float-right ts-p-2 ts-rounded-sm ts-uppercase"
              >
                <b>{{ $t('wholesale.discount') }}</b>
              </div>
            </td>
            <td class="ts-text-right">
              <ts-currency-input
                class="form-control ts-text-right"
                v-model.number="model.discount"
                @input="calculate"
              />
            </td>
            <td></td>
          </tr>
          <!-- govVat row: vat from vat_eligible=false products, display only. Always shown now -->
          <tr>
            <td colspan="7" class="ts-text-right">
              <div
                style="width: 17rem"
                class="ts-bg-blue-600 ts-text-white ts-float-right ts-p-2 ts-rounded-sm ts-uppercase"
              >
                <b>{{ $t('wholesale.govVat') }}</b>
              </div>
            </td>
            <td class="ts-text-right">
              <input
                type="text"
                class="ts-text-right ts-p-2 ts-border ts-border-gray-300 ts-rounded ts-w-full ts-outline-none"
                :value="
                  govVatAmount
                    ? $defaultCurrencyWithCode(govVatAmount, default_precision)
                    : 0
                "
                readonly
              />
            </td>
            <td></td>
          </tr>

          <!-- vatAmountCustomer row: vat from vat_eligible=true products, added to grand total. Always shown now -->
          <tr>
            <td colspan="7" class="ts-text-right">
              <div
                style="width: 17rem"
                class="ts-bg-blue-600 ts-text-white ts-float-right ts-p-2 ts-rounded-sm ts-uppercase"
              >
                <b>{{ $t('wholesale.vatAmountCustomer') }}</b>
              </div>
            </td>
            <td class="ts-text-right">
              <input
                type="text"
                class="ts-text-right ts-p-2 ts-border ts-border-gray-300 ts-rounded ts-w-full ts-outline-none"
                :value="
                  vatAmountCustomer
                    ? $defaultCurrencyWithCode(
                        vatAmountCustomer,
                        default_precision
                      )
                    : 0
                "
                readonly
              />
            </td>
            <td></td>
          </tr>
          <tr>
            <td colspan="7" class="ts-text-right">
              <div
                style="width: 17rem"
                class="ts-bg-blue-600 ts-text-white ts-float-right ts-p-2 ts-rounded-sm ts-uppercase"
              >
                <b>{{ $t('wholesale.despositAmount') }}</b>
              </div>
            </td>
            <td class="ts-text-right">
              <input
                type="text"
                class="ts-text-right ts-p-2 ts-border ts-border-gray-300 ts-rounded ts-w-full ts-outline-none"
                :value="
                  model.amount_deposit
                    ? $defaultCurrencyWithCode(
                        model.amount_deposit,
                        default_precision
                      )
                    : 0
                "
                readonly
              />
            </td>
            <td></td>
          </tr>
          <tr>
            <td colspan="7" class="ts-text-right">
              <div
                style="width: 17rem"
                class="ts-bg-blue-600 ts-text-white ts-float-right ts-p-2 ts-rounded-sm ts-uppercase"
              >
                <b>{{ $t('wholesale.grandTotal') }}</b>
              </div>
            </td>
            <td class="ts-text-right">
              <input
                type="text"
                class="ts-text-right ts-p-2 ts-w-full ts-text-red-700 ts-font-extrabold ts-border ts-border-gray-400 ts-rounded-sm ts-outline-none"
                :value="
                  model.grand_total
                    ? $defaultCurrencyWithCode(
                        model.grand_total,
                        default_precision
                      )
                    : 0
                "
                readonly
              />
            </td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
    <a-modal
      v-if="show_price"
      v-model="show_price"
      :footer="null"
      :zIndex="1020"
      :maskClosable="false"
      width="650px"
      :title="
        selectedProduct
          ? selectedProduct.barcode +
            '|' +
            selectedProduct.product_name_en +
            ' ($ ' +
            selectedProduct.cost +
            '|៛ ' +
            selectedProduct.cost * khrRate +
            ')'
          : 'N/A'
      "
    >
      <SoldPrice
        v-model="model"
        :selectedProduct="selectedProduct"
        @priceChange="priceChange()"
      />
    </a-modal>
    <a-modal
      v-if="show_modal_balance"
      v-model="show_modal_balance"
      :footer="null"
      :zIndex="1020"
      :maskClosable="false"
      width="650px"
      title="Ending Balance Other Warehouse"
    >
      <table class="ts-w-full">
        <thead>
          <tr>
            <th class="ts-uppercase ts-text-white ts-px-2 ts-py-1">No</th>
            <th class="ts-uppercase ts-text-white ts-px-2 ts-py-1">Barcode</th>
            <th class="ts-uppercase ts-text-white ts-px-2 ts-py-1">
              Product Name
            </th>
            <th class="ts-uppercase ts-text-white ts-px-2 ts-py-1">
              Warehouse
            </th>
            <th
              class="ts-uppercase ts-text-white ts-px-2 ts-py-1 ts-text-center"
            >
              Ending Balance
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(balance, index) in balanceOtherLocation" :key="index">
            <td class="ts-px-2 ts-py-1">{{ index + 1 }}</td>
            <td class="ts-px-2 ts-py-1">{{ balance.barcode }}</td>
            <td class="ts-px-2 ts-py-1">
              <p class="ts-m-0">{{ balance.product_name_kh }}</p>
              <p class="ts-m-0">{{ balance.product_name_en }}</p>
            </td>
            <td class="ts-px-2 ts-py-1">{{ balance.location_name_en }}</td>
            <td class="ts-px-2 ts-py-1 ts-text-center">
              {{ balance.ending_balance }}
            </td>
          </tr>
        </tbody>
      </table>
    </a-modal>
    <a-drawer
      v-if="show_serial_input"
      :title="addSerial.product_name_en"
      :width="400"
      :zIndex="1020"
      :visible="show_serial_input"
      :body-style="{ paddingBottom: '80px' }"
      @close="() => (show_serial_input = false)"
    >
      <AddProductSerialManual
        v-model="addSerial"
        @calculate="calculate"
        :product_detail="model.product_detail"
      />
    </a-drawer>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapGetters, mapState } from 'vuex'
import AddProductSerialManual from './add-product-serial-manual.vue'
import AddAutoAfterSearchSerial from './auto-after-search-serial.vue'
import SoldPrice from './sold-price.vue'
export default {
  name: 'itemDetails',
  props: ['value', 'validate'],
  components: {
    AddProductSerialManual,
    AddAutoAfterSearchSerial,
    SoldPrice
  },
  data() {
    return {
      errors: new Errors(),
      show_serial_input: false,
      show_modal_balance: false,
      show_price: false,
      balanceOtherLocation: [],
      selectedProduct: [],
      product_id: '',
      addSerial: {
        product_name_en: null
      },
      model: {}
    }
  },
  computed: {
    ...mapGetters('wholesale/operation', [
      'getUom',
      'getSalePrice',
      'getDiscount',
      'getMax',
      'getMin',
      'getCost'
    ]),
    ...mapState('wholesale/operation', ['setting']),
    isAccounting() {
      return this.setting.is_accounting
    },
    numRow() {
      return this.model.product_detail.length
    },
    countQty() {
      return _.sumBy(this.model.product_detail, 'quantity')
    },
    sumUSDPrice() {
      return _.sumBy(this.model.product_detail, 'unit_price')
    },
    totalAmountUSDPrice() {
      return _.sumBy(this.model.product_detail, 'grand_total')
    },
    khrRate() {
      let foundFx = this.$store.state.wholesale.operation.exchangeRate.find(
        e => e.to_currency == 'KHR'
      )
      if (foundFx) {
        return (this.model.fx_rate_khr = parseFloat(foundFx.fx_rate))
      }
      return 4000
    },
    locationAssign() {
      return this.$store.state.authUser.user.warehouses
        .filter(w => !w.pivot.is_default)
        .map(el => el.location_id)
    },
    isNotAllowToEdit: () => product => {
      // discount free item per amount
      if (product.promo_type_id == 3) {
        return product.unit_price == 0
      }
      // discount buy item free item
      if (product.promo_type_id == 4) {
        return (
          product.promo_ref_promo_product_id != '' &&
          product.promo_ref_promo_product_id != undefined &&
          product.promo_ref_promo_product_id > 0
        )
      }

      return false
    },
    locale() {
      return this.$root.$i18n.locale
    },
    default_currency_sign() {
      return this.$store.state.authUser.sys_setting.default_currency_sign
    },
    default_precision() {
      return this.$store.state.authUser.sys_setting.decimal_digit
    },
    // vat from vat_eligible=false products — display only in govVat row, NOT added to grand_total
    govVatAmount() {
      return _.round(
        _.sumBy(
          this.model.product_detail.filter(d => !d.vat_eligible),
          d => (d.total_after_discount * (d.vat_rate || 0)) / 100
        ),
        this.default_precision
      )
    },

    // vat from vat_eligible=true products — display in vatAmountCustomer row, added to grand_total
    vatAmountCustomer() {
      return _.round(
        _.sumBy(
          this.model.product_detail.filter(d => d.vat_eligible),
          d => (d.total_after_discount * (d.vat_rate || 0)) / 100
        ),
        this.default_precision
      )
    }
  },
  created() {
    this.model = this.value
  },
  methods: {
    ...mapActions('wholesale/operation', ['getFormViewData', 'productBalance']),
    removeRow(row) {
      // remove product which related promotion
      const productionProduct = this.model.product_detail.filter(
        detail => detail.promo_ref_promo_product_id == row.product_id
      )
      productionProduct.forEach(el => {
        this.model.product_detail.splice(
          this.model.product_detail.indexOf(el),
          1
        )
      })
      // end remove product related promotion
      this.model.product_detail.splice(
        this.model.product_detail.indexOf(row),
        1
      )
      this.calculate()
    },
    onUomChange(row) {
      row.unit_price = this.getSalePrice({
        product_id: row.product_id,
        uom_id: row.uom_id
      })
      row.discount_rate = this.getDiscount({
        product_id: row.product_id,
        uom_id: row.uom_id
      })
      row.cost = this.getCost({
        product_id: row.product_id,
        uom_id: row.uom_id
      })
      this.calculate()
    },
    onKhrInput(row) {
      row.unit_price = row.amount_in_khr / this.khrRate
      this.calculate()
    },
    onUpUsdInput(row, value) {
      row.unit_price = parseFloat(value) || 0
      row.amount_in_khr = row.unit_price * this.khrRate
      this.calculate()
    },
    calculate() {
      if (this.model.vat_in === true && !this.model.vat_rate) {
        this.model.vat_rate = 10
      }

      this.model.product_detail.forEach(detail => {
        // Step 1: Calculate base amount (unit_price x quantity)
        var amount = _.round(
          detail.unit_price * detail.quantity,
          this.default_precision
        )

        // Validate discount before applying
        // % discount cannot exceed 100
        if (detail.discount_type == '%' && detail.discount_rate > 100) {
          detail.discount_rate = 100
        }
        // fixed amount discount cannot exceed base amount
        if (
          detail.discount_type == this.default_currency_sign &&
          detail.discount_rate > amount
        ) {
          detail.discount_rate = amount
        }

        // Step 2: Calculate discount amount based on discount type
        if (detail.discount_type == this.default_currency_sign) {
          // Fixed amount discount (e.g. $5 off)
          detail.discount_amount = detail.discount_rate
        }
        if (detail.discount_type == '%') {
          // Percentage discount (e.g. 10% off)
          detail.discount_amount = (amount * detail.discount_rate) / 100
        }

        // Step 3: Subtract discount from base amount
        detail.total_after_discount = _.round(
          amount - detail.discount_amount,
          this.default_precision
        )

        // Step 4: Calculate vat at product level
        // vat is always tracked separately now, never baked into line grand_total,
        // regardless of vat_in. vat_eligible=false defaults to 10% (government vat, display only).
        if (!detail.vat_eligible) {
          detail.vat_rate = detail.vat_rate || 10
        }
        detail.vat_amount = _.round(
          (detail.total_after_discount * (detail.vat_rate || 0)) / 100,
          this.default_precision
        )
        detail.grand_total = _.round(
          detail.total_after_discount,
          this.default_precision
        )
      })

      this.model.discount = this.model.discount ? this.model.discount : 0

      // Step 5: sub_total is always the sum of base (pre-vat) line totals now
      this.model.sub_total = _.round(
        _.sumBy(this.model.product_detail, 'total_after_discount'),
        this.default_precision
      )

      // Validate order-level discount cannot exceed subtotal + vat
      const maxDiscount = _.round(
        this.model.sub_total + this.model.vat_amount,
        this.default_precision
      )

      if (this.model.discount > maxDiscount) {
        this.model.discount = maxDiscount
      }

      this.model.total_after_discount = _.round(
        this.model.sub_total - this.model.discount,
        this.default_precision
      )

      // Step 7: order-level vat, always computed from vat_eligible=true lines,
      // regardless of vat_in. vat from vat_eligible=false lines is display-only via govVatAmount.
      this.model.vat_amount = _.round(
        _.sumBy(
          this.model.product_detail.filter(d => d.vat_eligible),
          d => (d.total_after_discount * (d.vat_rate || 0)) / 100
        ),
        this.default_precision
      )

      // Step 8: grand_total = base subtotal + vat from vat_eligible=true products only - deposit
      this.model.grand_total = _.round(
        this.model.total_after_discount +
          this.model.vat_amount -
          this.model.amount_deposit,
        this.default_precision
      )

      // Step 9: amount_paid = grand_total, amount_due = 0 by default commented out to make default amount paide 0 for ref sale contract create
      // this.model.amount_paid = this.model.grand_total
      // this.model.amount_due = 0
    },
    addSerialProduct(product) {
      this.addSerial = product
      this.show_serial_input = true
    },
    viewPrice(product) {
      this.show_price = true
      this.selectedProduct = product
      this.product_id = product.product_id
    },
    priceChange() {
      let row = this.model.product_detail.find(
        e => e.product_id == this.product_id
      )
      this.onUpUsdInput(row)
    },
    getProductBalance() {
      var product = this.model.product_detail.map(el => el.product_id)
      if (product.length) {
        this.$emit('loading', true)
        this.productBalance({
          product_id: product
        })
          .then(response => {
            let data = response.data
            this.model.product_detail.forEach(
              el =>
                (el.balance = data.find(
                  p => p.product_id == el.product_id
                ).ending_balance)
            )
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
          .finally(() => {
            this.$emit('loading', false)
          })
      }
    },
    showBalance(record) {
      this.$emit('loading', true)
      this.productBalance({
        product_id: [record.product_id],
        location_id: this.locationAssign
      })
        .then(response => {
          this.balanceOtherLocation = response.data
          this.show_modal_balance = true
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          this.$emit('loading', false)
        })
    }
  },
  watch: {
    // recalculate whenever vat_in  changes so product grand_totals update immediately
    'model.vat_in': function(newVal) {
      this.calculate()
    },
    validate: function(error) {
      this.errors = new Errors(error.errors)
    }
  }
}
</script>

<style scoped>
.max-h {
  height: 610px;
  max-height: 620px;
  overflow: scroll;
}
table {
  /* white-space: nowrap; */
  margin: 0;
  /* border-collapse: separate; */
  /* border-spacing: 0;  */
  /* table-layout: fixed; */
  /* border: 1px solid black; */
}
table thead {
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: rgb(22, 102, 145);
  color: white;
}
table tfoot {
  position: sticky;
  bottom: 0;
  z-index: 1;
  background-color: white;
}
</style>
